import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';

import {
  applySiteChatPlacement,
  preconfigureSiteChat,
  setSiteChatContact,
  SITE_CHAT_READY_EVENT,
} from '@/lib/siteChat';
import { useDashboardChromeMounted } from '@/lib/siteChatVisibility';

import {
  getSiteChatPlacement,
  SITE_CHAT_BOT_URL,
  SITE_CHAT_LAUNCHER,
  SITE_CHAT_SCRIPT_URL,
} from '@/constants/siteChat';
import { UserContext } from '@/utils/useUser';

const SCRIPT_ID = 'site-chat-widget';
/**
 * The launcher waits for the page's `load` event and then for browser idle
 * time, so it never competes with a landing page's own resources — but a busy
 * page (polling, session recording, dev HMR) can starve `requestIdleCallback`
 * for many seconds, which is why the idle wait is capped. `next/script`'s
 * `lazyOnload` is the same idea without the cap.
 */
const IDLE_TIMEOUT_MS = 2000;

/**
 * Site-wide support chat bubble (our own Bots widget), mounted once in _app.
 *
 * The launcher script is injected the FIRST time a page wants the bubble and
 * then kept alive across client-side navigations; route changes only
 * `show()`/`hide()` it (dashboard pages) or `destroy()` it (pages that embed a
 * different widget). Nothing is loaded on a full page load of a dashboard
 * route, so product pageviews cost no bots-server requests.
 */
export default function SiteChatWidget() {
  const router = useRouter();
  const dashboardChromeMounted = useDashboardChromeMounted();
  // Static marketing pages render without MyUserContextProvider, so read the
  // context directly and treat "no provider" as "not signed in".
  const userContext = useContext(UserContext);
  const contactEmail = userContext?.user?.email ?? null;
  const contactName =
    [userContext?.userDetails?.first_name, userContext?.userDetails?.last_name]
      .filter(Boolean)
      .join(' ') || null;
  const contactId = userContext?.user?.id ?? null;
  const placement = getSiteChatPlacement(router.pathname, {
    dashboardChromeMounted,
  });
  const placementRef = useRef(placement);
  placementRef.current = placement;

  // Latch: once loaded, the script stays for the rest of the SPA session.
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    preconfigureSiteChat(placement);
    applySiteChatPlacement(placement);
    if (placement !== 'show') return;
    // Defer the latch past this commit's other effects: on a full load of a
    // dashboard page the first render runs before the layout has registered
    // its presence, so placement reads 'show' for one tick. React re-renders
    // with 'hide' at the end of the same effect flush (synchronously), which
    // clears this timer — so product pageviews never load the launcher.
    const timer = setTimeout(() => setShouldLoad(true), 0);
    return () => clearTimeout(timer);
  }, [placement]);

  // Signed-in visitors are named in the support agent's Conversations view;
  // cleared again on sign-out so a shared browser never inherits the name.
  useEffect(() => {
    setSiteChatContact(
      contactEmail
        ? {
            email: contactEmail,
            name: contactName ?? undefined,
            externalId: contactId ?? undefined,
          }
        : null,
    );
  }, [contactEmail, contactName, contactId]);

  // The launcher may finish loading after the route already changed; re-apply
  // the current placement the moment it is ready (covers the new API) and on
  // the script's load event (covers a launcher without the API).
  useEffect(() => {
    const reapply = () => applySiteChatPlacement(placementRef.current);
    window.addEventListener(SITE_CHAT_READY_EVENT, reapply);
    return () => window.removeEventListener(SITE_CHAT_READY_EVENT, reapply);
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    let cancelled = false;
    let idleHandle: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const inject = () => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SITE_CHAT_SCRIPT_URL;
      script.async = true;
      // Read by the launcher via document.currentScript.
      script.setAttribute('data-chat-url', SITE_CHAT_BOT_URL);
      script.setAttribute('data-btn-position', SITE_CHAT_LAUNCHER.position);
      script.setAttribute('data-widget-btn-color', SITE_CHAT_LAUNCHER.color);
      script.onload = () => applySiteChatPlacement(placementRef.current);
      document.body.appendChild(script);
    };

    const whenIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(inject, {
          timeout: IDLE_TIMEOUT_MS,
        });
      } else {
        timer = setTimeout(inject, 1);
      }
    };

    if (document.readyState === 'complete') {
      whenIdle();
    } else {
      window.addEventListener('load', whenIdle, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', whenIdle);
      if (
        idleHandle !== undefined &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [shouldLoad]);

  return null;
}
